import { IconButton, ListItemButton, ListItemText } from "@mui/material";

import { DatasetRecord } from "@/shared/domain/datasets";
import { useDatasetFileCount } from "@/shared/domain/datasets/hooks/useDatasetFileCount";
import { RefreshIcon } from "@/shared/foundation/icons/mui";
import { pluralize } from "@/shared/text";

import styles from "./DatasetList.module.css";

interface DatasetListItemProps {
  dataset: DatasetRecord;
  orgId: string;
  isActive: boolean;
  onSelect: (datasetId: string) => void;
  getSelectedCountForDataset: (datasetId: string) => number;
}

export function DatasetListItem(props: DatasetListItemProps) {
  const { dataset, orgId, isActive, onSelect, getSelectedCountForDataset } =
    props;

  const selectedCount = getSelectedCountForDataset(dataset.dataset_id);

  return (
    <ListItemButton
      className={styles.listItem}
      selected={isActive}
      onClick={() => onSelect(dataset.dataset_id)}
    >
      <ListItemText
        primary={dataset.name || dataset.dataset_id}
        secondary={
          <div>
            <p>{dataset.dataset_id}</p>
            <p style={{ display: "flex", gap: "0.25rem" }}>
              <FileCount datasetId={dataset.dataset_id} orgId={orgId} />{" "}
              <SelectedCount count={selectedCount} />
            </p>
          </div>
        }
        primaryTypographyProps={{
          noWrap: true,
          title: dataset.name || dataset.dataset_id,
          className: styles.title,
        }}
        secondaryTypographyProps={{
          noWrap: true,
          variant: "caption",
          className: styles.subtitle,
        }}
      />
    </ListItemButton>
  );
}

function FileCount(props: { datasetId: string; orgId: string }) {
  const fileCountQuery = useDatasetFileCount(props.datasetId, props.orgId);

  if (fileCountQuery.isLoading) {
    return <>Loading...</>;
  }

  if (fileCountQuery.isError || fileCountQuery.data === undefined) {
    return (
      <>
        Couldn&apos;t get count{" "}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            void fileCountQuery.refetch();
          }}
          sx={{ padding: 0 }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </>
    );
  }

  return (
    <>{`${fileCountQuery.data} ${pluralize("file", fileCountQuery.data)}`}</>
  );
}

function SelectedCount(props: { count: number }) {
  if (props.count === 0) {
    return null;
  }
  return <span className={styles.selectedCount}>• {props.count} selected</span>;
}
